import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { progressApi, goalsApi } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Progress as ProgressBar } from '../components/ui/progress';
import { Flame, MessageCircle, Target as TargetIcon, BookOpen, TrendingUp, Sparkles, Trash2, Plus, CheckCircle2, Minus } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { logger } from '../lib/logger';

function formatDay(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
}

function StatTile({ icon: Icon, label, value, accent = false, testid }) {
  return (
    <Card className={`border-border ${accent ? 'bg-[#0a0d0e] text-white border-[#0a0d0e]' : ''}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Icon className={`h-4 w-4 ${accent ? 'text-[#4FBCC7]' : 'text-[#004C54]'}`} />
          <span className={`text-[11px] uppercase tracking-wider font-semibold ${accent ? 'text-white/60' : 'text-foreground/60'}`}>{label}</span>
        </div>
        <div className={`font-heading text-2xl font-bold ${accent ? 'text-white' : 'text-foreground'}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function GoalsSection({ goals, onCreate, onUpdate, onDelete, busy }) {
  const [title, setTitle] = useState('');
  const [target, setTarget] = useState(5);

  const submit = (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Give your goal a short title.');
      return;
    }
    onCreate({ title: title.trim(), target: Number(target) || 1, metric_type: 'sessions', current: 0 });
    setTitle('');
    setTarget(5);
  };

  return (
    <Card className="border-border" data-testid="goals-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <TargetIcon className="h-4 w-4 text-[#004C54]" />
          <h3 className="font-heading text-base font-bold">Your goals</h3>
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-2 mb-5" data-testid="goals-form">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Journal 5x this week"
            maxLength={160}
            data-testid="goal-title-input"
          />
          <Input
            type="number"
            min={1}
            max={1000}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="Target"
            data-testid="goal-target-input"
          />
          <Button type="submit" disabled={busy} className="bg-[#004C54] hover:bg-[#003a40] text-white" data-testid="goal-add-button">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </form>

        {goals.length === 0 && (
          <div className="text-sm text-foreground/60 bg-[#F6F7F8] rounded-lg p-4 text-center" data-testid="goals-empty">
            No goals yet. Add a small, specific weekly target to start.
          </div>
        )}

        {goals.length > 0 && (
          <ul className="space-y-2.5" data-testid="goals-list">
            {goals.map((g) => {
              const pct = g.target ? Math.min(100, Math.round((g.current / g.target) * 100)) : 0;
              const done = g.status === 'completed';
              return (
                <li key={g.goal_id} className={`rounded-lg border ${done ? 'border-[#004C54]/30 bg-[#F0F4F4]' : 'border-border bg-white'} p-3.5`} data-testid={`goal-${g.goal_id}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {done ? <CheckCircle2 className="h-4 w-4 text-[#004C54] shrink-0" /> : <TargetIcon className="h-4 w-4 text-foreground/40 shrink-0" />}
                      <span className={`text-[14px] font-semibold truncate ${done ? 'text-[#004C54]' : 'text-foreground'}`}>{g.title}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => onUpdate(g.goal_id, { current: Math.max(0, (g.current || 0) - 1) })}
                        aria-label="Decrease"
                        data-testid={`goal-dec-${g.goal_id}`}
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => onUpdate(g.goal_id, { current: Math.min(g.target, (g.current || 0) + 1) })}
                        aria-label="Increase"
                        data-testid={`goal-inc-${g.goal_id}`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-foreground/60 hover:text-destructive"
                        onClick={() => onDelete(g.goal_id)}
                        aria-label="Delete goal"
                        data-testid={`goal-del-${g.goal_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <ProgressBar value={pct} className="h-1.5 flex-1" />
                    <span className="text-[12px] font-mono tabular-nums text-foreground/60 shrink-0">{g.current}/{g.target}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default function Progress() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [ov, sm, gs] = await Promise.all([
        progressApi.overview(),
        progressApi.weeklySummary(),
        goalsApi.list(),
      ]);
      setOverview(ov);
      setSummary(sm);
      setGoals(gs?.goals || []);
    } catch (e) {
      logger.error('progress load failed', e);
      toast.error(e.message || 'Could not load progress.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const createGoal = async (payload) => {
    setBusy(true);
    try {
      const g = await goalsApi.create(payload);
      setGoals((prev) => [g, ...prev]);
      toast.success('Goal added.');
    } catch (e) { toast.error(e.message || 'Could not add goal.'); }
    finally { setBusy(false); }
  };

  const updateGoal = async (goal_id, patch) => {
    try {
      const g = await goalsApi.update(goal_id, patch);
      setGoals((prev) => prev.map((x) => (x.goal_id === goal_id ? g : x)));
    } catch (e) { toast.error(e.message || 'Could not update goal.'); }
  };

  const deleteGoal = async (goal_id) => {
    try {
      await goalsApi.remove(goal_id);
      setGoals((prev) => prev.filter((x) => x.goal_id !== goal_id));
    } catch (e) { toast.error(e.message || 'Could not delete goal.'); }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="progress-loading">
        <div>
          <Skeleton className="h-5 w-24 mb-2" />
          <Skeleton className="h-8 w-72" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const weekly = overview?.weekly_activity || [];
  const mood = overview?.mood_series || [];
  const themes = overview?.themes || [];
  const totals = overview?.totals || { coach: 0, pregame: 0, journal: 0 };
  const streak = overview?.streak_days || 0;

  // Adapt for charts
  const weeklyData = weekly.map((d) => ({ day: formatDay(d.date), Coach: d.coach, 'Pre-Game': d.pregame, Journal: d.journal }));
  const moodData = mood.map((d) => ({ day: formatDay(d.date), score: d.score }));

  return (
    <div className="space-y-6" data-testid="progress-page">
      <div>
        <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#004C54] font-semibold mb-2">
          Your progress
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Mental reps, measured</h1>
        <p className="text-[15px] text-foreground/70 mt-1 max-w-2xl">
          What gets tracked gets trained. Here's how your mind has been working out.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile icon={Flame} label="Current streak" value={`${streak} ${streak === 1 ? 'day' : 'days'}`} accent testid="stat-streak" />
        <StatTile icon={MessageCircle} label="Coach msgs" value={totals.coach} testid="stat-coach" />
        <StatTile icon={TargetIcon} label="Pre-Game plans" value={totals.pregame} testid="stat-pregame" />
        <StatTile icon={BookOpen} label="Journal entries" value={totals.journal} testid="stat-journal" />
      </div>

      {summary && (
        <Card className="border-2 border-[#004C54]/25 bg-gradient-to-br from-[#F0F4F4] to-white" data-testid="weekly-summary-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-[#004C54]" />
              <span className="text-[11px] uppercase tracking-wider font-semibold text-[#004C54]">This week</span>
            </div>
            <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-start">
              <div>
                <div className="flex flex-wrap gap-3 mb-3">
                  <Badge variant="outline" className="border-[#004C54]/30">{summary.totals.coach} coach msgs</Badge>
                  <Badge variant="outline" className="border-[#004C54]/30">{summary.totals.pregame} pre-game plans</Badge>
                  <Badge variant="outline" className="border-[#004C54]/30">{summary.totals.journal} journal entries</Badge>
                  {summary.avg_mood !== null && summary.avg_mood !== undefined && (
                    <Badge variant="outline" className="border-[#004C54]/30">avg mood {summary.avg_mood}/5</Badge>
                  )}
                </div>
                <p className="text-[14px] text-foreground/80 leading-relaxed" data-testid="weekly-focus">{summary.focus}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-border" data-testid="weekly-activity-card">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#004C54]" />
              <h3 className="font-heading text-base font-bold">Last 7 days</h3>
            </div>
            <span className="text-xs text-foreground/60">Stacked by tool</span>
          </div>
          <div className="h-56 -ml-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAEFEF" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#6B7373' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7373' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip cursor={{ fill: 'rgba(0,76,84,0.06)' }} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                <Bar dataKey="Coach" stackId="a" fill="#004C54" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Pre-Game" stackId="a" fill="#4FBCC7" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Journal" stackId="a" fill="#A5DBE1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-4 mt-2 text-[11px] text-foreground/60">
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#004C54]" /> Coach</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#4FBCC7]" /> Pre-Game</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#A5DBE1]" /> Journal</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border" data-testid="mood-trend-card">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#004C54]" />
              <h3 className="font-heading text-base font-bold">Mood trend (30 days)</h3>
            </div>
            <span className="text-xs text-foreground/60">Scale 1–5</span>
          </div>
          {moodData.length < 2 ? (
            <div className="text-sm text-foreground/60 bg-[#F6F7F8] rounded-lg p-4 text-center" data-testid="mood-empty">
              Add a few more journal entries with a mood word (e.g. "focused", "frustrated") to start seeing your trend.
            </div>
          ) : (
            <div className="h-56 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={moodData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAEFEF" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#6B7373' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11, fill: '#6B7373' }} axisLine={false} tickLine={false} width={20} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                  <Line type="monotone" dataKey="score" stroke="#004C54" strokeWidth={2.5} dot={{ r: 3, fill: '#004C54' }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border" data-testid="themes-card">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-[#004C54]" />
            <h3 className="font-heading text-base font-bold">Top themes from your journal</h3>
          </div>
          {themes.length === 0 ? (
            <div className="text-sm text-foreground/60 bg-[#F6F7F8] rounded-lg p-4" data-testid="themes-empty">
              Write a journal entry to surface what's on your mind.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="themes-list">
              {themes.map((t) => (
                <Badge
                  key={t.word}
                  variant="outline"
                  className="border-[#004C54]/30 text-foreground/80 capitalize"
                  style={{ fontSize: 12 + Math.min(4, t.count) }}
                >
                  {t.word} <span className="ml-1 text-foreground/40 font-mono text-[10px]">{t.count}</span>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <GoalsSection
        goals={goals}
        onCreate={createGoal}
        onUpdate={updateGoal}
        onDelete={deleteGoal}
        busy={busy}
      />

      <div className="flex flex-wrap items-center gap-3 text-sm text-foreground/60">
        <span>Keep the streak going —</span>
        <Button onClick={() => navigate('/app/coach')} variant="outline" size="sm" className="h-8" data-testid="progress-go-coach">Open AI Coach</Button>
        <Button onClick={() => navigate('/app/journal')} variant="outline" size="sm" className="h-8" data-testid="progress-go-journal">New journal entry</Button>
      </div>
    </div>
  );
}
